import React, { useContext, useEffect, useState } from 'react'
import { Button, makeStyles, tokens } from '@fluentui/react-components'
import { ArrowReset20Regular } from '@fluentui/react-icons'
import { ModalActions } from '../../common/modal/Modal'
import { ActionPlan, ActionPlanStatus } from '../../../api/schemas/schema'
import { useForm } from 'react-hook-form'
import { Grid } from '../../common/grid/Grid'
import { GridItem } from '../../common/grid/GridItem'
import { t } from '../../../i18n/i18n'
import { ActionDecision } from './modalContent/ActionDecision'
import { DataContext } from '../../../context/DataContext'
import { handleMessage } from '../../../helpers/stateHelper'
import { DraggableModal } from '../../common/modal/DraggableModal'
import { Form } from '../../common/form/Form'
import { ModalResetDecision } from './ModalResetDecision'

const useStyles = makeStyles({
   rightActions: {
      display: 'flex',
      gap: tokens.spacingHorizontalL,
      '@media screen and (max-width: 640px)': {
         flexDirection: 'column-reverse',
         gap: tokens.spacingHorizontalS,
      },
   },
})

type ModalActionPlanProps = {
   parentInformation: JSX.Element
   selectedItem: ActionPlan
   estimatedCost?: number
   close: () => void
   disabled: boolean
   onSubmit: (data: ActionPlan) => Promise<boolean>
}
export const ModalActionPlan = ({ parentInformation, selectedItem, estimatedCost, close, disabled, onSubmit }: ModalActionPlanProps) => {
   const dataContext = useContext(DataContext)
   const { userAccount } = dataContext.state
   const classes = useStyles()
   const [confirmResetOpen, setConfirmResetOpen] = useState(false)
   const [resettingDecision, setResettingDecision] = useState(false)
   const actionPlanForm = useForm<ActionPlan>({
      defaultValues: {
         actionNeeded: true,
         budgetedCost: selectedItem?.status !== ActionPlanStatus.Decided ? selectedItem?.budgetedCost : estimatedCost,
         responsible: userAccount,
         responsibleId: userAccount.id,
      },
      disabled: disabled,
   })
   const {
      reset,
      watch,
      handleSubmit,
      formState: { isDirty, isSubmitting },
   } = actionPlanForm

   useEffect(() => {
      if (!selectedItem) return

      const item = selectedItem
      const selectedCost = item.status !== ActionPlanStatus.Decided ? item.budgetedCost ?? estimatedCost ?? null : item.budgetedCost

      reset({
         ...item,
         budgetedCost: selectedCost,
         responsible: selectedItem.responsible ?? userAccount,
         responsibleId: selectedItem.responsibleId ?? userAccount.id,
      })
   }, [selectedItem?.id, estimatedCost, reset, selectedItem, userAccount])

   const saveForm = async (data: ActionPlan) => {
      data.modifiedBy = userAccount
      data.modifiedById = userAccount.id

      if (data.status === ActionPlanStatus.NotDecided || data.status === ActionPlanStatus.NotToBeAdressed) {
         data.decidedBy = userAccount
         data.decidedById = userAccount.id
      }

      if (data.actionNeeded) {
         data.status = ActionPlanStatus.Decided
      } else {
         data.status = ActionPlanStatus.NotToBeAdressed
      }

      const success = await onSubmit(data)
      if (success) {
         reset({ actionNeeded: true })
         close()
         handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('ActionPlan') }))
      } else {
         handleMessage(dataContext.setRootState, 'error', t('ValidationFailed', { Item: t('ActionPlan') }))
      }
   }

   const confirmResetDecision = async () => {
      setResettingDecision(true)
      try {
         const actionPlan = actionPlanForm.getValues()

         actionPlan.status = ActionPlanStatus.NotDecided
         actionPlan.actionNeeded = true
         actionPlan.modifiedBy = userAccount
         actionPlan.modifiedById = userAccount.id
         actionPlan.decidedAction = null
         actionPlan.responsible = null
         actionPlan.responsibleId = null
         actionPlan.priority = null
         actionPlan.deadline = null
         actionPlan.decidedActionComment = null
         actionPlan.decidedBy = null
         actionPlan.decidedById = null
         actionPlan.budgetedCost = null

         const success = await onSubmit(actionPlan)
         if (success) {
            reset({ actionNeeded: true })
            setConfirmResetOpen(false)
            close()
            handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('ActionPlan') })) // RESET
         } else {
            handleMessage(dataContext.setRootState, 'error', t('ValidationFailed', { Item: t('ActionPlan') }))
            setConfirmResetOpen(false)
         }
      } finally {
         setResettingDecision(false)
      }
   }

   const closeAndReset = () => {
      reset({ actionNeeded: true })
      close()
   }

   return (
      <>
         <DraggableModal open={selectedItem ? true : false} onClose={closeAndReset} title={t('DecisionOnAction')}>
            {selectedItem ? (
               <Form onSubmit={handleSubmit(saveForm)}>
                  <Grid>
                     {parentInformation}
                     <ActionDecision actionPlanForm={actionPlanForm} />
                     <GridItem size="12/12">
                        <ModalActions>
                           <Button onClick={closeAndReset} disabled={isSubmitting}>
                              {disabled ? t('Close') : t('Cancel')}
                           </Button>
                           <span className={classes.rightActions}>
                              <Button
                                 appearance="secondary"
                                 icon={<ArrowReset20Regular />}
                                 onClick={() => setConfirmResetOpen(true)}
                                 disabled={watch('status') === ActionPlanStatus.NotDecided || disabled}
                              >
                                 {t('ResetDecision')}
                              </Button>
                              {!disabled ? (
                                 <Button appearance="primary" type="submit" disabled={!isDirty || isSubmitting}>
                                    {t('Save')}
                                 </Button>
                              ) : null}
                           </span>
                        </ModalActions>
                     </GridItem>
                  </Grid>
               </Form>
            ) : null}
         </DraggableModal>
         <ModalResetDecision open={confirmResetOpen} close={() => setConfirmResetOpen(false)} onConfirm={confirmResetDecision} isSubmitting={resettingDecision} />
      </>
   )
}
